import React from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import "../../assets/styles/local/AppDatePicker.css";

dayjs.extend(customParseFormat);

const toDayjs = (value, format = "DD-MM-YYYY") => {
  if (!value) return null;

  if (dayjs.isDayjs(value)) return value;

  if (moment.isMoment(value)) {
    return dayjs(value.toDate());
  }

  if (value instanceof Date) {
    return dayjs(value);
  }

  const parsed = dayjs(value, format, true);
  return parsed.isValid() ? parsed : dayjs(value);
};

const CustomDatePicker = ({
  value,
  selected,
  className,
  style,
  format = "DD-MM-YYYY",
  dateFormat,
  onChange,
  variant = "borderless",
  multiple = false,
  allowClear = false,
  onKeyDown,
  prefix,
  suffixIcon,
  renderExtraFooter,
  minDateForPlanBoard,
  onOpenChange,
  onPanelChange,
  onPickerValueChange,
  ...rest
}) => {
  // Support both value and selected
  const pickerValue = value ?? selected;

  const normalizeFormat = (fmt) => {
    if (!fmt) return "DD/MM/YYYY";

    return fmt
      .replace(/dd/g, "DD")
      .replace(/yyyy/g, "YYYY")
      .replace(/yy/g, "YY");
  };
  // Support both format and dateFormat
  const pickerFormat = normalizeFormat(dateFormat || format);
  const selectedDate = React.useMemo(
    () => toDayjs(pickerValue, pickerFormat),
    [pickerValue, pickerFormat],
  );
  const selectedDateKey = selectedDate?.valueOf();
  const [panelDate, setPanelDate] = React.useState(
    () => selectedDate || dayjs(),
  );
  const previousSelectedDateKey = React.useRef(selectedDateKey);

  React.useEffect(() => {
    if (selectedDate && selectedDateKey !== previousSelectedDateKey.current) {
      setPanelDate(selectedDate);
    }

    previousSelectedDateKey.current = selectedDateKey;
  }, [selectedDate, selectedDateKey]);

  const handlePickerValueChange = (date, info) => {
    if (date) {
      setPanelDate(date);
    }

    if (onPickerValueChange) {
      onPickerValueChange(date, info);
    }
  };

  const handlePanelChange = (date, mode) => {
    if (date) {
      setPanelDate(date);
    }

    if (onPanelChange) {
      onPanelChange(date, mode);
    }
  };

  const handleOpenChange = (open) => {
    if (open) {
      setPanelDate(selectedDate || dayjs());
    }

    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  return (
    <DatePicker
      // size="large"
      className={`w-auto p-0 ${className || ""}`}
      style={{ minWidth: "90px ", ...style }}
      placeholder="Select a Date"
      value={selectedDate}
      pickerValue={panelDate}
      format={pickerFormat}
      onPickerValueChange={handlePickerValueChange}
      onPanelChange={handlePanelChange}
      onOpenChange={handleOpenChange}
      onChange2={(date, dateString) => {
        if (onChange) {
          onChange(date ? date.toDate() : null, dateString);
        }
      }}
      onChange={(date, dateString) => {
        if (onChange) {
          onChange(
            date
              ? new Date(date.year(), date.month(), date.date(), 12, 0, 0)
              : null,
            dateString,
          );
        }
      }}
      onKeyDown={onKeyDown}
      variant={variant}
      prefix={null}
      suffixIcon={null}
      allowClear={allowClear}
      multiple={multiple}
      showWeek={false}
      renderExtraFooter={renderExtraFooter}
      disabledDate={(current) =>
        minDateForPlanBoard
          ? current && current.isBefore(dayjs(minDateForPlanBoard), "day")
          : false
      }
      {...rest}
    />
  );
};

export default CustomDatePicker;
